/**
 * TEMP-H-003: Artifact Thumbnail Component
 * 
 * Displays artifact thumbnails with lazy loading, fallbacks,
 * and click-to-open functionality.
 */

import { useState, useRef, useEffect } from 'react';
import { 
  ExternalLink, 
  Download, 
  FileText, 
  Image as ImageIcon, 
  Video, 
  Music, 
  File,
  AlertCircle,
  Loader2
} from 'lucide-react';
import type { ArtifactRef } from '@/types/execution-events';

interface ArtifactThumbProps {
  artifact: ArtifactRef;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showName?: boolean;
  showSize?: boolean;
  onClick?: (artifact: ArtifactRef) => void;
  className?: string;
}

type LoadState = 'loading' | 'loaded' | 'error';

export function ArtifactThumb({ 
  artifact, 
  size = 'md',
  showName = true,
  showSize = false,
  onClick,
  className = ''
}: ArtifactThumbProps) {
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Size configurations
  const sizeClasses = {
    xs: 'w-8 h-8',
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24'
  };

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4', 
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getFileTypeIcon = () => {
    const iconClass = `${iconSizes[size]} text-gray-400`;
    
    switch (artifact.type) {
      case 'image':
        return <ImageIcon className={iconClass} />;
      case 'video':
        return <Video className={iconClass} />;
      case 'audio':
        return <Music className={iconClass} />;
      case 'text':
        return <FileText className={iconClass} />;
      case 'json':
        return <FileText className={iconClass} />;
      default:
        return <File className={iconClass} />;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)}${units[unitIndex]}`;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClick = () => {
    if (onClick) {
      onClick(artifact);
    } else if (artifact.publicUrl) {
      window.open(artifact.publicUrl, '_blank');
    }
  };

  const handleImageLoad = () => {
    setLoadState('loaded');
  };

  const handleImageError = () => {
    setLoadState('error');
  };

  const renderThumbnail = () => {
    // For images, try to load thumbnail or fallback to icon
    if (artifact.type === 'image' && isVisible) {
      const thumbnailUrl = artifact.thumbnail || artifact.publicUrl;
      
      if (thumbnailUrl && loadState !== 'error') {
        return (
          <>
            <img
              ref={imgRef}
              src={thumbnailUrl}
              alt={artifact.name}
              className={`${sizeClasses[size]} object-cover rounded transition-opacity ${
                loadState === 'loaded' ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
            {loadState === 'loading' && (
              <div className={`absolute inset-0 flex items-center justify-center ${sizeClasses[size]} bg-gray-800 rounded`}>
                <Loader2 className={`${iconSizes[size]} text-gray-400 animate-spin`} />
              </div>
            )}
          </>
        );
      }
    }

    // Fallback: show file type icon
    return (
      <div className={`${sizeClasses[size]} bg-gray-800 rounded flex items-center justify-center border border-gray-700`}>
        {loadState === 'error' ? (
          <AlertCircle className={`${iconSizes[size]} text-red-400`} />
        ) : (
          getFileTypeIcon()
        )}
      </div>
    );
  };

  const renderOverlay = () => {
    if (size === 'xs') return null;

    return (
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
        <div className="flex items-center gap-1">
          {artifact.publicUrl && (
            <ExternalLink className="w-3 h-3 text-white" />
          )}
          <Download className="w-3 h-3 text-white" />
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={containerRef}
      className={`group relative flex-shrink-0 ${className}`}
    >
      {/* Thumbnail */}
      <div 
        className="relative cursor-pointer"
        onClick={handleClick}
        title={artifact.name}
      >
        {renderThumbnail()}
        {renderOverlay()}
        
        {/* Type badge */}
        {size !== 'xs' && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-gray-700 rounded-full flex items-center justify-center border border-gray-600">
            <span className="text-xs">{getFileTypeIcon()}</span>
          </div>
        )}
      </div>

      {/* File Info */}
      {(showName || showSize) && size !== 'xs' && (
        <div className="mt-1 space-y-0.5">
          {showName && (
            <div className="text-xs text-gray-300 truncate max-w-[80px]" title={artifact.name}>
              {artifact.name}
            </div>
          )}
          
          {showSize && (
            <div className="text-xs text-gray-500 space-x-1">
              {artifact.size && <span>{formatFileSize(artifact.size)}</span>}
              {artifact.duration && <span>({formatDuration(artifact.duration)})</span>}
              {artifact.dimensions && (
                <span>{artifact.dimensions.width}×{artifact.dimensions.height}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}